/**
 * WelcomeModal.js
 * Reference walkthrough for Constellation KG, opened from the My Account welcome card.
 *
 * This is reference material people return to, not a first-run tour. One modal shell
 * serves four independent sections; each entry point on My Account opens the shell
 * scoped to its own subset of panels via the `section` prop. Step dots, the step
 * counter, Back/Next and the final-step behavior all scope to the filtered subset.
 *
 * Sections: getting-started (9) | knowledge-entry (6) | explorer (3) | beyond-basics (3)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import useFocusTrap from '../../hooks/useFocusTrap';
import Lightbox from '../common/Lightbox/Lightbox';
import './WelcomeModal.css';

// =========================================
// Screenshots
// =========================================
// Colocated with this component (project convention — there is no src/assets tree).
// All are well above CRA's 10,000-byte inline threshold, so each is emitted as a
// hashed file and fetched only when its panel renders. shotThoughtsTagsSave serves
// three panels (My Thoughts / My Tags / Save Your Entry): imported once, referenced
// three times.
import shotWebStore from './CKG-04-chrome-web-store.webp';
import shotPermissionWarning from './CKG-05-chrome-permission-warning.webp';
import shotToolbar from './CKG-07-extension-in-toolbar.webp';
import shotThoughtsTagsSave from './CKG-11-extension-thoughts-tags-save.webp';
import shotKnowledgeEntries from './CKG-12-my-knowledge-entries.webp';
import shotAiExplorer from './CKG-13-ai-explorer-synthesis.webp';
import shotConstellations from './CKG-14-constellations.webp';
import shotCollectiveIntelligence from './CKG-15-collective-intelligence.webp';
import shotJournal from './CKG-16-my-journal.webp';
import shotUnexploredLinks from './CKG-17-unexplored-links.webp';

// Cropped detail shots (prototype pass). These are pre-cropped files displayed
// directly — no crop rectangles, transforms, or zoom logic. Still PNG while the
// rest of the set is WebP; format conversion is a separate pass.
import cropLoggingIn from './CROP-logging-in.webp';
import cropChangePassword from './CROP-change-password.webp';
import cropAddExtension from './CROP-add-extension.webp';
import cropPinExtension from './CROP-pin-extension.webp';
import cropExtensionLogin from './CROP-extension-login.webp';
import cropFirstEntry from './CROP-first-knowledge-entry.webp';
import cropTitleUrlDate from './CROP-title-url-date.webp';
import cropAiExplorer from './CROP-ai-explorer.webp';

// =========================================
// Sections
// =========================================

export const WELCOME_SECTIONS = {
  GETTING_STARTED: 'getting-started',
  KNOWLEDGE_ENTRY: 'knowledge-entry',
  EXPLORER: 'explorer',
  BEYOND_BASICS: 'beyond-basics'
};

// =========================================
// Panels
// =========================================
// Each panel: { id, section, title, subtitle, image, alt, content }.
// `image: null` renders the centered .welcome-intro treatment instead of the
// two-column .step-with-screenshot layout.

const ALL_STEPS = [
  // ---------- Section 1: Getting Started ----------
  {
    id: 'welcome',
    section: 'getting-started',
    title: 'Welcome to Constellation KG',
    subtitle: 'Explore your knowledge',
    image: null,
    alt: null,
    content: (
      <div className="welcome-intro">
        <p>
          We all come across information we find interesting and important every day.
          Over time, the volume becomes unmanageable.
        </p>
        <p>
          We forget what we learned. We can't find a piece of information we know exists.
          In short, we lose sight of the forest for the trees.
        </p>
        <p>
          Constellation KG takes the tedious aspects of knowledge management off your
          hands. In doing so, you can ground artificial intelligences in your thinking —
          while honoring and protecting your privacy.
        </p>
        <p>
          The net result? Through CKG, AI becomes more of a thinking partner than an
          "answer machine." Labs make powerful AIs; you and CKG make those AIs personal.
        </p>
      </div>
    )
  },
  {
    id: 'logging-in',
    section: 'getting-started',
    title: 'Logging In',
    subtitle: 'Getting into Constellation KG',
    image: cropLoggingIn,
    alt: 'The Constellation KG login card: an Email field, a Password field with a show-password control, the Login button, and the "Forgot your password?" link beneath it',
    content: (
      <>
        <p className="step-description">
          Once you've received your password, go to www.constellationkg.com and log in.
        </p>
        <p className="step-description">
          Forgot your password? No problem — click the "Forgot your password?" link and a
          magic link will be sent to your e-mail address.
        </p>
      </>
    )
  },
  {
    id: 'change-password',
    section: 'getting-started',
    title: 'Change Your Password',
    subtitle: 'Make it yours',
    image: cropChangePassword,
    alt: 'The Change Password panel on the My Account page, with Current Password, New Password, and Confirm New Password fields above a Change Password button',
    content: (
      <p className="step-description">
        Once you're logged in, go to your My Account page and change your password.
      </p>
    )
  },
  {
    id: 'add-extension',
    section: 'getting-started',
    title: 'Add the Extension',
    subtitle: 'Your gateway to capturing knowledge',
    image: cropAddExtension,
    alt: 'The "Welcome to Constellation KG!" card on the My Account page, with the full-width "Add to Chrome" button above the four reference-section buttons',
    content: (
      <>
        <p className="step-description">
          On your My Account page, click the "Add to Chrome" button. This will take you to
          the Chrome Web Store, where you'll add the Constellation KG extension to your
          browser.
        </p>
        <p className="step-description">
          The browser extension is how you capture passages of text from the Internet and
          make them part of your knowledge base.
        </p>
      </>
    )
  },
  {
    id: 'chrome-web-store',
    section: 'getting-started',
    title: 'The Chrome Web Store',
    subtitle: 'One click to install',
    image: shotWebStore,
    alt: "Constellation KG's Chrome Web Store listing with the Add to Chrome button",
    content: (
      <p className="step-description">
        When you're on Constellation KG's Chrome Web Store page, click "Add to Chrome."
      </p>
    )
  },
  {
    id: 'permission-warning',
    section: 'getting-started',
    title: 'About That Warning',
    subtitle: 'What Chrome is telling you',
    image: shotPermissionWarning,
    alt: "Chrome's extension permission warning dialog",
    content: (
      <>
        <p className="step-description">
          You'll then see an ominously worded warning. Google wants you to be aware that
          the extension will capture information from the page that you're looking at. You
          will when you choose to make a knowledge entry for information contained on the
          page: CKG tries to grab the article's title, URL, and publication date for you to
          save you some typing.
        </p>
        <p className="step-description">
          Worth saying again: CKG does not track or change the pages that you're looking at.
        </p>
        <p className="step-description">
          Click "Add extension."
        </p>
      </>
    )
  },
  {
    id: 'pin-extension',
    section: 'getting-started',
    title: 'Pin the Extension',
    subtitle: 'Keep it within reach',
    image: cropPinExtension,
    alt: "Chrome's Extensions menu open, listing Constellation KG under Full access with a pin control beside it",
    content: (
      <p className="step-description">
        Click the little puzzle piece next to the search bar and you'll see the
        Constellation KG extension. Click the pin next to it to add it to the tool bar
        so you can use the extension easily.
      </p>
    )
  },
  {
    id: 'toolbar-button',
    section: 'getting-started',
    title: 'Your Toolbar Button',
    subtitle: 'There it is',
    image: shotToolbar,
    alt: "The Constellation KG extension button pinned in Chrome's toolbar",
    content: (
      <p className="step-description">
        You'll now see a miniature version of Constellation KG's logo as a button in the
        tool bar.
      </p>
    )
  },
  {
    id: 'extension-login',
    section: 'getting-started',
    title: 'Log In to the Extension',
    subtitle: 'One last step',
    image: cropExtensionLogin,
    alt: 'The Constellation KG extension panel open in the browser, showing its Email and Password fields, the Login button, and a notice that you are not currently logged in',
    content: (
      <>
        <p className="step-description">
          Click the button for Constellation KG's browser extension and log in.
        </p>
        <p className="step-description note">
          (Yes, you need to log into the extension separately the first time. We're
          working on that…)
        </p>
      </>
    )
  },

  // ---------- Section 2: Making a Knowledge Entry ----------
  {
    id: 'first-entry',
    section: 'knowledge-entry',
    title: 'Your First Knowledge Entry',
    subtitle: 'Capture what catches your attention',
    image: cropFirstEntry,
    alt: 'The Constellation KG extension panel open beside an article, with Title, URL, and Date Published filled in automatically and an empty My Focus box',
    content: (
      <>
        <p className="step-description">
          You're ready to make your first knowledge entry!
        </p>
        <p className="step-description">
          Highlight a passage of text you find interesting on the page you're reading,
          right-click, and choose "Make a CKG Focus" — or you can copy the highlighted
          passage and paste it into the extension's My Focus text box.
        </p>
      </>
    )
  },
  {
    id: 'focus-metadata',
    section: 'knowledge-entry',
    title: 'Title, URL, and Date',
    subtitle: 'Captured for you',
    image: cropTitleUrlDate,
    alt: 'A close view of the extension panel\'s Title, URL, and Date Published fields, filled in automatically from the article',
    content: (
      <>
        <p className="step-description">
          The extension captures the document title, URL, and publication date for you.
        </p>
        <p className="step-description">
          If the title or publication date is incorrect, you can type over it.
        </p>
      </>
    )
  },
  {
    id: 'my-thoughts',
    section: 'knowledge-entry',
    title: 'My Thoughts',
    subtitle: 'Optional, but valuable',
    image: shotThoughtsTagsSave,
    alt: 'The browser extension My Thoughts text box with the microphone button',
    content: (
      <>
        <p className="step-description">
          "My Thoughts" is a place for you to record your reactions to the Focus. Thoughts
          are optional, but they help contextualize why the text is interesting or
          important to you.
        </p>
        <p className="step-description">
          If you don't want to interrupt your flow, you can add a Thought to an entry
          later through the My Knowledge Entries page.
        </p>
        <p className="step-description">
          You can type a Thought or click the microphone and use CKG's speech-to-text
          capability. CKG transcribes what you say — we do not collect or store any audio.
        </p>
      </>
    )
  },
  {
    id: 'my-tags',
    section: 'knowledge-entry',
    title: 'My Tags',
    subtitle: 'Where lateral thinking lives',
    image: shotThoughtsTagsSave,
    alt: 'The browser extension My Tags field with tags entered',
    content: (
      <>
        <p className="step-description">
          My Tags are optional but they help you with search, especially when you use tags
          to associate a Focus or your Thought with other ideas and concepts that might
          not be obvious based on a literal reading of the text alone (i.e., tags become
          an expression of your lateral or tangential thinking). Your sense of the text is
          unique to you, and how it relates to other ideas is unique to you. Lean into
          that!
        </p>
        <p className="step-description">
          Simply enter the words or phrases you want to use as tags as a comma-delimited
          string (e.g., a, b, c). Hashmarks (#) are unnecessary.
        </p>
      </>
    )
  },
  {
    id: 'save-entry',
    section: 'knowledge-entry',
    title: 'Save Your Entry',
    subtitle: 'And back to reading',
    image: shotThoughtsTagsSave,
    alt: 'The browser extension Save button at the bottom of the capture form',
    content: (
      <>
        <p className="step-description">
          Once your entry is complete, click "Save" and it will be added to the database
          with your other entries.
        </p>
        <p className="step-description">
          You can also log out of the extension in this window if you choose.
        </p>
      </>
    )
  },
  {
    id: 'knowledge-entries',
    section: 'knowledge-entry',
    title: 'My Knowledge Entries',
    subtitle: 'Your personal knowledge repository',
    image: shotKnowledgeEntries,
    alt: 'The My Knowledge Entries view with search and Filter by Source controls',
    content: (
      <>
        <p className="step-description">
          The entries you make using the browser extension show up in My Knowledge
          Entries. You can search your entries, filter them by Source, edit your Thoughts
          (to include adding Thoughts), or delete a Thought or the whole entry there.
        </p>
        <p className="step-description">
          If you have unidentified sources, click on Unidentified and you can correct them
          there. For example, if the unidentified source is www.nytimes.com, you can
          correct it to read "New York Times".
        </p>
      </>
    )
  },

  // ---------- Section 3: AI Explorer & Constellations ----------
  {
    id: 'ai-explorer',
    section: 'explorer',
    title: 'AI Explorer',
    subtitle: 'Ask questions of your knowledge base',
    image: cropAiExplorer,
    alt: 'An AI Explorer response to the question "Have I captured anything about daydreaming and creativity?", with a summary paragraph above a list of collapsed themes and a PDF download link',
    content: (
      <>
        <p className="step-description">
          AI Explorer uses Anthropic's AIs to help you surface patterns in your thinking
          across your Knowledge Entries and your Journal Entries.
        </p>
        <p className="step-description">
          When you ask a question like, "Have I captured anything about daydreaming and
          creativity?", CKG searches your knowledge base and passes the relevant entries
          (based on the Focus, your Thoughts [if present], and Tags [again, if present])
          to Anthropic for analysis.
        </p>
        <p className="step-description">
          The information passed to Anthropic does not tie the information to your name or
          to your e-mail address.
        </p>
      </>
    )
  },
  {
    id: 'themes',
    section: 'explorer',
    title: 'Themes',
    subtitle: 'The detail behind the analysis',
    image: shotAiExplorer,
    alt: 'An expanded theme within an AI Explorer synthesis',
    content: (
      <>
        <p className="step-description">
          AI Explorer looks for patterns that exist across the Knowledge and Journal
          entries related to your question and presents them as themes. The themes are
          initially collapsed so that you can quickly skim the results.
        </p>
        <p className="step-description">
          The analysis is followed by a source list that shows you the basis of the AI's
          assertions. Again, the AI only knows that the entries came from CKG: your name
          or e-mail address is never shared with the AI.
        </p>
        <p className="step-description">
          As your knowledge base grows, the AI Explorer's analyses will change. CKG grows
          with you.
        </p>
        <p className="step-description">
          Lastly, you can download an analysis as a .pdf for future use.
        </p>
      </>
    )
  },
  {
    id: 'constellations',
    section: 'explorer',
    title: 'Constellations',
    subtitle: 'The shape of your attention',
    image: shotConstellations,
    alt: 'The Constellations view visualizing knowledge entries used in an analysis',
    content: (
      <>
        <p className="step-description">
          Constellations is a simple visualization of the documents used in the AI's
          analysis relative to your query. The size of each "star" gives a sense of how
          much you interacted with the document that was the source of a Focus or inspired
          a Thought. Brighter stars are the sources the AI drew on in its analysis; dimmer
          ones were read but not cited.
        </p>
        <p className="step-description">
          If the Constellation is sparse, see if there are entries in the Collective
          Intelligence and add them to your knowledge base. Or write a Journal Entry. Or
          use the extension to create new Knowledge Entries. Each new entry enriches AI
          Explorer's analysis and gives you deeper insights into your thinking.
        </p>
      </>
    )
  },

  // ---------- Section 4: Beyond the Basics ----------
  {
    id: 'collective-intelligence',
    section: 'beyond-basics',
    title: 'Collective Intelligence',
    subtitle: 'Search and discovery as a team sport',
    image: shotCollectiveIntelligence,
    alt: 'The Collective Intelligence search view',
    content: (
      <>
        <p className="step-description">
          When you or any other member of the CKG community makes a Knowledge Entry, a
          copy of the focus is sent to CKG's Collective Intelligence (CI). The CI stores
          the Focus and information about the document it comes from without revealing
          information about who submitted the Focus (or other Focuses that they might have
          submitted).
        </p>
        <p className="step-description">
          You can add Focuses that you find interesting by checking the box next to the
          focus and then clicking the book icon at the top of the column.
        </p>
        <p className="step-description">
          The Collective Intelligence is built in a way that you should never see a Focus
          that you already have in your My Knowledge Entries.
        </p>
        <p className="step-description">
          When you add something from the CI to your knowledge entries, you'll be given
          the opportunity to make the entry your own by adding a Thought and tags.
        </p>
      </>
    )
  },
  {
    id: 'journal',
    section: 'beyond-basics',
    title: 'My Journal',
    subtitle: 'Not all of your thoughts are tied to a web page',
    image: shotJournal,
    alt: 'The My Journal entry view with the microphone button',
    content: (
      <>
        <p className="step-description">
          When you have a thought that is not tied to a web-based article or video, you
          can make an entry in My Journal.
        </p>
        <p className="step-description">
          Journal entries are treated like the Thoughts you attach to Knowledge Entries:
          they are private and, when included in an AI Explorer analysis, do not contain
          your name or e-mail address.
        </p>
        <p className="step-description">
          As with the browser extension, you can type your entry or click the microphone
          and have CKG transcribe the entry for you.
        </p>
      </>
    )
  },
  {
    id: 'unexplored-links',
    section: 'beyond-basics',
    title: 'Unexplored Links',
    subtitle: 'A reading list that builds itself',
    image: shotUnexploredLinks,
    alt: 'The Unexplored Links view listing collected links and their source passages',
    content: (
      <>
        <p className="step-description">
          When you make a knowledge entry using the browser extension, any embedded
          hyperlinks in your focus are collected and stored in Unexplored Links.
          Constellation KG then attempts to collect the document title for the link and
          display it on this page (and show you the passage from which the link was
          collected).
        </p>
        <p className="step-description feedback-note">
          Title capture is still a work in progress — where we can't resolve a title,
          you'll see the raw URL. We're very interested in hearing whether you find this
          feature useful.
        </p>
      </>
    )
  }
];

// =========================================
// Component
// =========================================

const WelcomeModal = ({ isOpen, onClose, section }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const modalRef = useRef(null);
  const screenshotButtonRef = useRef(null);

  // =========================================
  // Focus Trap
  // =========================================
  // Suspended while the Lightbox is open: the Lightbox portals to document.body,
  // outside modalRef, so leaving this active would have the two traps fight over
  // every Tab and yank focus back into the covered modal.
  useFocusTrap(modalRef, isOpen && !lightboxOpen);

  // =========================================
  // Escape Key Handler
  // =========================================
  // Also gated on !lightboxOpen. Both this listener and the Lightbox's are on
  // `document`, so without the gate a single Escape closes the Lightbox AND the
  // whole modal underneath it.
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen && !lightboxOpen) {
        onClose();
      }
    };

    if (isOpen && !lightboxOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, onClose, lightboxOpen]);

  // =========================================
  // Reset on open / section change
  // =========================================
  // Reference material, not a resumable tour — reopening a section always starts
  // at its first panel. Also drops any stale Lightbox state.
  useEffect(() => {
    setCurrentStep(0);
    setLightboxOpen(false);
  }, [isOpen, section]);

  const handleLightboxClose = useCallback(() => setLightboxOpen(false), []);

  if (!isOpen) return null;

  // Panels for THIS section. Everything below — dots, counter, nav, final step —
  // scopes to this filtered subset, never to ALL_STEPS.
  const steps = ALL_STEPS.filter((step) => step.section === section);
  if (steps.length === 0) return null;

  // currentStep can briefly exceed the new section's bounds between the render
  // that changes `section` and the reset effect; clamp so we never index past the end.
  const safeStep = Math.min(currentStep, steps.length - 1);
  const currentStepData = steps[safeStep];
  const isLastStep = safeStep === steps.length - 1;
  const isFirstStep = safeStep === 0;

  const handleNext = () => {
    if (safeStep < steps.length - 1) {
      setCurrentStep(safeStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrev = () => {
    if (safeStep > 0) {
      setCurrentStep(safeStep - 1);
    }
  };

  const handleStepClick = (index) => {
    setCurrentStep(index);
  };

  return (
    <>
      <div className="welcome-modal-overlay" onClick={onClose}>
        <div
          className="welcome-modal"
          onClick={(e) => e.stopPropagation()}
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="welcome-modal-title"
        >
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            ×
          </button>

          {/* Progress indicator */}
          <div className="step-progress">
            {steps.map((step, index) => (
              <button
                key={step.id}
                className={`step-dot ${index === safeStep ? 'active' : ''} ${index < safeStep ? 'completed' : ''}`}
                onClick={() => handleStepClick(index)}
                aria-label={`Go to step ${index + 1}: ${step.title}`}
              >
                <span className="step-dot-inner"></span>
              </button>
            ))}
          </div>

          {/* Step content */}
          <div className="step-content">
            <div className="step-header">
              <h2 id="welcome-modal-title">{currentStepData.title}</h2>
              <p className="step-subtitle">{currentStepData.subtitle}</p>
            </div>
            <div className="step-body">
              {currentStepData.image ? (
                <div className="step-with-screenshot">
                  <div className="step-text">{currentStepData.content}</div>
                  {/* A real button, not a click handler on the <img>: keyboard
                      activation and focus come for free. */}
                  <button
                    type="button"
                    className="step-screenshot"
                    onClick={() => setLightboxOpen(true)}
                    ref={screenshotButtonRef}
                    aria-label={`Enlarge screenshot: ${currentStepData.title}`}
                  >
                    <img src={currentStepData.image} alt={currentStepData.alt} />
                  </button>
                </div>
              ) : (
                currentStepData.content
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className="modal-navigation">
            <button
              className="nav-button prev"
              onClick={handlePrev}
              disabled={isFirstStep}
            >
              ← Back
            </button>
            <span className="step-counter">
              {safeStep + 1} of {steps.length}
            </span>
            <button
              className="nav-button next"
              onClick={handleNext}
            >
              {isLastStep ? 'Close' : 'Next →'}
            </button>
          </div>
        </div>
      </div>

      {/* Rendered OUTSIDE .welcome-modal-overlay on purpose. The Lightbox portals to
          document.body, but React synthetic events bubble through the REACT tree, not
          the DOM tree — nested inside the overlay, a click on the Lightbox backdrop
          would also fire the overlay's onClose and dismiss this modal underneath it. */}
      {currentStepData.image && (
        <Lightbox
          isOpen={lightboxOpen}
          onClose={handleLightboxClose}
          src={currentStepData.image}
          alt={currentStepData.alt}
          ariaLabel={`Screenshot: ${currentStepData.title}`}
          returnFocusRef={screenshotButtonRef}
        />
      )}
    </>
  );
};

export default WelcomeModal;
